import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Home as HomeIcon } from 'lucide-react'

/**
 * 店舗・スタッフ・運営の画面。ここでは既定の Home ボタンを**出さない**。
 *
 * `/home` は顧客のホームで、店舗管理から飛ぶ先として意味を成さない
 * (店員が客画面に落ちるだけで、戻る導線にならない)。
 *
 * #1361 で「onBack があれば既定で出す」に倒したとき、店舗側は各ページで
 * `onHome={false}` を書いて opt-out する運用になった。が、それは #1361 自身が
 * 問題視した「新しいページを足すたびに付け忘れる」構造をそのまま裏返しただけで、
 * 実際 #1365 で追加した在庫2画面と体験チケット消し込みが付け忘れていた。
 * ページ側の規律に委ねず、ここで判定する。
 */
const STAFF_ROUTE_PREFIXES = ['/store', '/staff', '/admin']

interface HeaderProps {
  title: string
  onBack?: () => void
  rightAction?: ReactNode
  /**
   * Home escape hatch. 戻るボタンの隣に家アイコンを出す。
   *
   * #1361: **`onBack` があれば既定で出す**ようにした。以前は任意 prop で、
   * 47 ページ中 4 ページにしか付いておらず、新しいページを足すたびに
   * 付け忘れていた。逃げ道は「想定できていないループ」に効くものなので、
   * 個別ページの判断に委ねると意味を成さない。
   *
   * 行き先を変えたいページだけ関数を渡す。`false` を渡すと非表示
   * （ホーム自身や、ホームへ出ると文脈が壊れる画面向け）。
   *
   * 店舗・スタッフ・運営の画面 (STAFF_ROUTE_PREFIXES) では**既定で出ない**。
   * 明示的に関数を渡した場合だけ出る。
   */
  onHome?: (() => void) | false
}

export default function Header({ title, onBack, rightAction, onHome }: HeaderProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isStaffRoute = STAFF_ROUTE_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  )
  // 既定の逃げ道。onBack があるページ = 階層の途中なので、必ず出口を用意する。
  // ただし顧客画面に限る (店舗側から /home へ出しても迷子になるだけ)。
  const homeAction =
    onHome === false
      ? null
      : (onHome ?? (onBack && !isStaffRoute ? () => navigate('/home') : null))
  return (
    <header className="relative flex items-center justify-center bg-white h-14 px-4 shrink-0">
      {/* Back + optional home buttons grouped left */}
      {(onBack || homeAction) && (
        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center">
          {onBack && (
            <button
              onClick={onBack}
              className="w-10 h-10 flex items-center justify-center rounded-full tap-effect hover:bg-gray-50 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={22} className="text-text-primary" />
            </button>
          )}
          {homeAction && (
            <button
              onClick={homeAction}
              className="w-10 h-10 flex items-center justify-center rounded-full tap-effect hover:bg-gray-50 transition-colors"
              aria-label="Go home"
            >
              <HomeIcon size={20} className="text-text-primary" />
            </button>
          )}
        </div>
      )}

      {/* Title */}
      <h1 className="font-display font-semibold text-[18px] text-text-primary truncate max-w-[60%]">
        {title}
      </h1>

      {/* Right action */}
      {rightAction && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          {rightAction}
        </div>
      )}
    </header>
  )
}
